import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Clock3, Eye, Flag, Link2, LockKeyhole, MailWarning, MessageSquareWarning, ShieldCheck, UserRoundCheck } from "lucide-react";
import { useQuiz } from "../context/QuizContext";
import quizService from "../services/quizService";
import { Page, PageContainer, PageHeader } from "../components/ui/page";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Toast } from "../components/ui/feedback";
import { Badge } from "../components/ui/data-display";

const tips = [
  ["Check the domain", "I2E’s official domain is i2econsulting.com. Treat lookalike domains with suspicion.", ShieldCheck],
  ["Verify the sender", "Look for misspellings, added words, and substituted characters in email addresses.", UserRoundCheck],
  ["Inspect every link", "Hover before clicking so you can compare the displayed text with the true destination.", Link2],
  ["Resist urgency", "Account closures, missed payments, and immediate demands are common pressure tactics.", Clock3],
  ["Protect credentials", "IT will never ask you to share your password or sensitive credentials by email.", LockKeyhole],
  ["Notice unusual content", "Generic greetings, unexpected attachments, and process shortcuts deserve scrutiny.", MailWarning],
  ["Confirm unusual requests", "Verify messages from managers, vendors, or HR through a separate trusted channel.", MessageSquareWarning],
  ["Report uncertainty", "When in doubt, stop and contact IT security through the approved reporting process.", Flag],
];

const EducationPage = () => {
  const navigate = useNavigate();
  const { userInfo, setFlowStage } = useQuiz();
  const [canStart, setCanStart] = useState(null);
  const [showRetakeNotice, setShowRetakeNotice] = useState(false);

  const checkEligibility = async () => {
    if (!userInfo?.email) {
      setCanStart(false);
      return;
    }

    try {
      const permission = await quizService.canStartQuiz(userInfo.email);
      setCanStart(Boolean(permission?.can_start));
    } catch {
      setCanStart(false);
    }
  };

  useEffect(() => {
    checkEligibility();
  }, [userInfo?.email]);

  useEffect(() => {
    if (!userInfo?.email) return undefined;
    const interval = setInterval(checkEligibility, 5000);
    return () => clearInterval(interval);
  }, [userInfo?.email]);

  useEffect(() => {
    if (!showRetakeNotice) return undefined;
    const timeout = setTimeout(() => setShowRetakeNotice(false), 4000);
    return () => clearTimeout(timeout);
  }, [showRetakeNotice]);

  const start = () => {
    if (!canStart) {
      setShowRetakeNotice(true);
      return;
    }
    setFlowStage("education_done");
    navigate("/quiz");
  };

  return (
    <Page>
      <PageContainer size="xl" className="xl:py-5">
        <PageHeader
          eyebrow="Learning module"
          title="Before you begin"
          description="Review the signals that help separate trusted communication from a phishing attempt."
          icon={Eye}
        />

        <div className="space-y-5">
          <Card>
            <CardContent className="p-5 sm:p-6">
              <div className="mb-5 flex items-center justify-between gap-3">
                <div>
                  <h2 className="section-title">Eight warning signs</h2>
                  <p className="mt-1 secondary-text">Take a moment to read each one before starting.</p>
                </div>
                <Badge variant="primary">2–3 min read</Badge>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {tips.map(([title, body, Icon], index) => (
                  <article key={title} className="flex gap-3 rounded-md border border-border bg-surface-muted/50 p-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-subtle text-primary">
                      <Icon className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-semibold text-muted-foreground">0{index + 1}</span>
                        <h3 className="text-sm font-semibold leading-5 text-foreground">{title}</h3>
                      </div>
                      <p className="mt-1.5 text-xs leading-5 text-muted-foreground">{body}</p>
                    </div>
                  </article>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="text-center">
            {canStart && (
              <p className="mb-3 text-sm text-muted-foreground">
                <AlertTriangle className="mr-2 inline h-4 w-4" />
                Your progress cannot be resumed if you leave during the assessment.
              </p>
            )}

            <Button size="lg" variant={canStart === false ? "outline" : "primary"} className="w-full sm:w-auto sm:min-w-56" disabled={canStart === null} onClick={start}>
              Start assessment
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {showRetakeNotice && (
          <Toast variant="warning" title="Retake unavailable" onClose={() => setShowRetakeNotice(false)}>
            You’ve already completed this assessment. Contact an administrator to enable another attempt.
          </Toast>
        )}
      </PageContainer>
    </Page>
  );
};

export default EducationPage;
